import type { AxiosInstance } from "axios";
import type { Socket } from "socket.io-client";
import { isSupportMessage, mergeMessages, normalizeMessage, readMessages, readSentMessage } from "./supportMessages.ts";
import type { SupportMessage } from "./supportMessages.ts";
import { customerCareStorageKey } from "./customerCareState.ts";
import { classifyConversation, getPayloadChatId, registerCustomerCareConversation } from "../../utils/conversationBoundary.ts";

const CHAT_API = "https://ilosiwaju-mbaay-2025.com/api/v1/admin";
const NOTIFICATION_API = "https://ilosiwaju-mbaay-2025.com/api/v1/notifications";
const REQUEST_TIMEOUT = 20000;

export interface SupportNotification {
  _id: string;
  message: string;
  createdAt: string;
  read: boolean;
}

export interface SupportChatState {
  chatId: string | null;
  isReady: boolean;
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
  connectionError: string | null;
  notificationError: string | null;
  notifications: SupportNotification[];
  isAgentTyping: boolean;
}

export const initialSupportChatState = (): SupportChatState => ({
  chatId: null, isReady: false, isLoading: false, isSending: false, error: null,
  connectionError: null, notificationError: null, notifications: [], isAgentTyping: false,
});

export interface SupportChatSessionOptions {
  accountType: "user" | "vendor";
  senderId: string;
  chatId: string | null;
  api: Pick<AxiosInstance, "get" | "post">;
  createSocket: (token: string) => Socket;
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem">;
  isCurrentAccount: () => boolean;
  getMessages: () => SupportMessage[];
  setMessages: (messages: SupportMessage[]) => void;
  setChatId: (id: string | null) => void;
  onState: (state: SupportChatState) => void;
  onIncoming: (message: SupportMessage) => void;
}

const record = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null ? value as Record<string, unknown> : null;

const idFrom = (value: unknown): string | null => {
  if (typeof value === "string" && value.trim()) return value;
  const data = record(value);
  return typeof data?._id === "string" && data._id.trim() ? data._id : null;
};

const failureMessage = (error: unknown, fallback: string): string => {
  const data = record(record(record(error)?.response)?.data);
  return typeof data?.message === "string" ? `${fallback} ${data.message}` : fallback;
};

// One session owns the socket and asynchronous work for one authenticated account.
// Message reads come from the store so handlers never overwrite a stale React snapshot.
export class SupportChatSession {
  private options: SupportChatSessionOptions;
  private active = true;
  private started = false;
  private token = "";
  private socket: Socket | null = null;
  private abort = new AbortController();
  private starting: Promise<void> | null = null;
  private history: Promise<void> | null = null;
  private loadingNotifications: Promise<void> | null = null;
  private typingTimer: ReturnType<typeof setTimeout> | null = null;
  private agentTypingTimer: ReturnType<typeof setTimeout> | null = null;
  private isTyping = false;
  private verifiedChatId: string | null = null;
  private state: SupportChatState;

  constructor(options: SupportChatSessionOptions) {
    this.options = options;
    this.state = { ...initialSupportChatState(), chatId: options.chatId };
  }

  private current() { return this.active && this.options.isCurrentAccount(); }

  private update(patch: Partial<SupportChatState>) {
    if (!this.current()) return;
    this.state = { ...this.state, ...patch };
    this.options.onState(this.state);
  }

  private requestConfig() {
    return { signal: this.abort.signal, timeout: REQUEST_TIMEOUT };
  }

  private storeChatId(id: string | null) {
    if (!this.current()) return;
    this.options.setChatId(id);
    this.update({ chatId: id, isReady: Boolean(id) });
    this.verifiedChatId = id;
    if (id) registerCustomerCareConversation({ role: this.options.accountType, id: this.options.senderId }, id);
    try {
      const key = customerCareStorageKey(this.options.accountType, this.options.senderId);
      if (id) this.options.storage.setItem(key, id);
      else this.options.storage.removeItem(key);
      this.options.storage.removeItem(`chatId-${this.options.senderId}`);
    } catch { /* Redux still keeps the conversation when browser storage is unavailable. */ }
  }

  private belongsToCustomerCare(payload: unknown, requireThread = false, expectedChatId = this.state.chatId) {
    const data = record(payload);
    const chatId = getPayloadChatId(payload);
    if (data?.isCustomerCareChat === false || record(data?.chat)?.isCustomerCareChat === false || classifyConversation(payload, { role: this.options.accountType, id: this.options.senderId }) === "customer_vendor") return false;
    return (!requireThread || Boolean(chatId)) && (!chatId || chatId === expectedChatId);
  }

  private putMessages(incoming: SupportMessage[]) {
    if (!this.current()) return;
    this.options.setMessages(mergeMessages(this.options.getMessages(), incoming));
  }

  updateToken(token: string) {
    if (!this.current() || this.token === token) return;
    this.token = token;
    if (this.started) this.connectSocket();
  }

  private connectSocket() {
    this.socket?.removeAllListeners();
    this.socket?.disconnect();
    if (!this.current() || !this.token) return;
    const socket = this.options.createSocket(this.token);
    this.socket = socket;
    socket.on("connect", () => {
      if (!this.current() || this.socket !== socket) return;
      this.update({ connectionError: null });
      if (this.state.chatId && this.verifiedChatId === this.state.chatId) {
        socket.emit("joinChat", this.state.chatId);
        void this.refreshHistory(true);
      }
    });
    socket.on("disconnect", (reason: string) => {
      if (this.socket !== socket || !this.current()) return;
      this.stopTyping();
      this.update({ connectionError: "Live updates are disconnected. Reconnecting…", isAgentTyping: false });
      if (reason === "io server disconnect") socket.connect();
    });
    socket.on("connect_error", () => {
      if (this.socket === socket) this.update({ connectionError: "Live updates are unavailable. You can still send messages or refresh the conversation." });
    });
    socket.on("customerCareChatStarted", (payload: unknown) => {
      if (!this.current() || this.socket !== socket) return;
      const data = record(payload);
      const id = idFrom(data?._id);
      // The active conversation is established by its authenticated HTTP request.
      if (!id || !this.state.chatId || this.verifiedChatId !== id || this.state.chatId !== id || !this.belongsToCustomerCare(payload)) return;
      const owner = idFrom(data?.userId ?? data?.user ?? data?.customer);
      if (owner && owner !== this.options.senderId) return;
      this.storeChatId(id);
      socket.emit("joinChat", id);
      void this.refreshHistory(true);
    });
    socket.on("customerCareMessage", (payload: unknown) => {
      if (!this.current() || this.socket !== socket || !this.state.chatId || this.verifiedChatId !== this.state.chatId || !this.belongsToCustomerCare(payload, true)) return;
      const message = normalizeMessage(payload, this.options.senderId);
      if (!message) return;
      const alreadyReceived = this.options.getMessages().some(existing => existing.id === message.id);
      this.putMessages([message]);
      if (message.sender !== "user" && !alreadyReceived) {
        this.update({ isAgentTyping: false });
        this.options.onIncoming(message);
        void this.refreshNotifications();
      }
    });
    socket.on("typing", (payload: unknown) => {
      const data = record(payload);
      if (!this.current() || this.socket !== socket || this.verifiedChatId !== this.state.chatId || !this.belongsToCustomerCare(payload, true) || idFrom(data?.sender) === this.options.senderId) return;
      if (this.agentTypingTimer) clearTimeout(this.agentTypingTimer);
      this.update({ isAgentTyping: true });
      this.agentTypingTimer = setTimeout(() => this.update({ isAgentTyping: false }), 5000);
    });
    socket.on("stopTyping", (payload: unknown) => {
      const data = record(payload);
      if (this.current() && this.socket === socket && this.verifiedChatId === this.state.chatId && this.belongsToCustomerCare(payload, true) && idFrom(data?.sender) !== this.options.senderId) {
        if (this.agentTypingTimer) clearTimeout(this.agentTypingTimer);
        this.update({ isAgentTyping: false });
      }
    });
  }

  start(): Promise<void> {
    if (!this.current()) return Promise.resolve();
    if (this.starting) return this.starting;
    if (!this.started) {
      this.started = true;
      this.connectSocket();
    } else if (!this.socket?.connected) this.socket?.connect();
    this.update({ isLoading: true, error: null });
    this.starting = this.initialize().finally(() => {
      this.starting = null;
      this.update({ isLoading: false });
    });
    return this.starting;
  }

  private async initialize() {
    try {
      if (this.current()) {
        const stored = this.options.getMessages();
        const existing = Array.isArray(stored) ? stored : [];
        const recovered = existing
          .filter(isSupportMessage)
          .filter(message => !(message.id.startsWith("auto-") && message.sender === "bot" && message.content === "Welcome to Mbaay Support! We're here to assist you. An agent will respond shortly."))
          .map(message => message.deliveryStatus === "sending" && !this.state.isSending ? { ...message, deliveryStatus: "failed" as const } : message);
        if (stored !== existing || recovered.length !== existing.length || recovered.some((message, index) => message !== existing[index])) this.options.setMessages(recovered);
      }
      if (!this.state.chatId) {
        let storedId: string | null = null;
        try {
          storedId = this.options.storage.getItem(customerCareStorageKey(this.options.accountType, this.options.senderId)) ?? this.options.storage.getItem(`chatId-${this.options.senderId}`);
        } catch { /* Optional cache. */ }
        // Legacy storage is a candidate only. Verify through the authenticated
        // admin-history endpoint before registering it or joining its room.
        if (storedId && storedId !== "undefined" && storedId !== "null") this.update({ chatId: storedId });
      }
      if (this.state.chatId) {
        try {
          await this.loadHistory(this.state.chatId);
        } catch (error) {
          const status = record(record(error)?.response)?.status;
          if (status !== 404 && status !== 410) throw error;
          // A removed conversation must not trap the user in a permanent retry loop.
          this.storeChatId(null);
          if (this.current()) this.options.setMessages([]);
        }
      }
      if (!this.current()) return;
      if (!this.state.chatId) {
        const response = await this.options.api.post(`${CHAT_API}/start-customer-care`, { userId: this.options.senderId }, this.requestConfig());
        if (!this.current()) return;
        const data = record(response.data);
        const newId = idFrom(data?.chatId ?? data?.chat);
        if (!newId) throw new Error("The server did not return a conversation ID.");
        if (!this.belongsToCustomerCare(response.data, false, newId)) throw new Error("The server returned a marketplace conversation for Customer Care.");
        this.storeChatId(newId);
        this.socket?.emit("joinChat", newId);
        await this.loadHistory(newId);
      } else {
        this.socket?.emit("joinChat", this.state.chatId);
      }
      this.update({ error: null });
      void this.refreshNotifications();
    } catch (error) {
      this.update({ error: failureMessage(error, "Could not load your support conversation. Please try again.") });
    }
  }

  private async loadHistory(chatId: string) {
    const response = await this.options.api.get(`${CHAT_API}/customer_care_chatmessages/${chatId}`, this.requestConfig());
    if (!this.current() || chatId !== this.state.chatId) return;
    const data = record(response.data);
    if (!Array.isArray(data?.messages)) throw new Error("Invalid conversation response.");
    if (!this.belongsToCustomerCare(response.data)) throw new Error("The server returned a marketplace conversation for Customer Care.");
    this.storeChatId(chatId);
    this.putMessages(readMessages({ messages: data.messages.filter(message => this.belongsToCustomerCare(message)) }, this.options.senderId));
  }

  refreshHistory(quiet = false): Promise<void> {
    if (!this.current() || !this.state.chatId) return Promise.resolve();
    if (this.history) return this.history;
    const chatId = this.state.chatId;
    this.history = this.loadHistory(chatId).then(() => {
      if (!quiet) this.update({ error: null });
    }).catch(error => {
      this.update({ error: failureMessage(error, "Could not refresh the conversation. Please try again.") });
    }).finally(() => { this.history = null; });
    return this.history;
  }

  refreshNotifications(): Promise<void> {
    if (!this.current()) return Promise.resolve();
    if (this.loadingNotifications) return this.loadingNotifications;
    this.loadingNotifications = this.options.api.get(`${NOTIFICATION_API}/allnotifications/${this.options.senderId}`, this.requestConfig()).then(response => {
      const data = record(response.data);
      const notifications = Array.isArray(data?.notifications) ? data.notifications.flatMap((value: unknown) => {
        const notification = record(value);
        // The account-notification endpoint also serves marketplace activity.
        // Only a reference to the verified support thread belongs here.
        if (this.verifiedChatId !== this.state.chatId || !this.belongsToCustomerCare(value, true)) return [];
        if (typeof notification?._id !== "string" || typeof notification.message !== "string" || typeof notification.createdAt !== "string" || !Number.isFinite(Date.parse(notification.createdAt))) return [];
        return [{ _id: notification._id, message: notification.message, createdAt: notification.createdAt, read: notification.read === true }];
      }) : [];
      this.update({ notifications, notificationError: null });
    }).catch(() => {
      this.update({ notificationError: "Notifications could not be refreshed." });
    }).finally(() => { this.loadingNotifications = null; });
    return this.loadingNotifications;
  }

  async send(content: string): Promise<boolean> {
    const text = content.trim();
    if (!this.current() || !text || !this.state.chatId || this.verifiedChatId !== this.state.chatId || this.state.isLoading || this.state.isSending) return false;
    const chatId = this.state.chatId;
    this.update({ isSending: true, error: null });
    this.stopTyping();
    const failed = this.options.getMessages().find(message => message.sender === "user" && message.isOptimistic && message.deliveryStatus === "failed" && message.content === text);
    const tempId = failed?.tempId ?? failed?.id ?? `support-${crypto.randomUUID()}`;
    const pending: SupportMessage = { id: tempId, tempId, content: text, sender: "user", timestamp: new Date().toISOString(), isOptimistic: true, deliveryStatus: "sending" };
    try {
      if (failed) {
        // A failed HTTP response can still have reached the server. Check history
        // before retrying, and keep the correlation ID if a resend is needed.
        await this.loadHistory(chatId);
        if (!this.current() || chatId !== this.state.chatId) return false;
        const reconciled = this.options.getMessages().find(message => message.tempId === tempId || message.id === failed.id);
        if (reconciled && !reconciled.isOptimistic) {
          this.update({ error: null });
          return true;
        }
      }
      this.putMessages([pending]);
      const response = await this.options.api.post(`${CHAT_API}/send-message`, { chatId, content: text, senderId: this.options.senderId, tempId }, {
        ...this.requestConfig(),
        headers: this.socket?.connected && this.socket.id ? { "x-socket-id": this.socket.id } : {},
      });
      if (!this.current() || chatId !== this.state.chatId) return false;
      const responseRecord = record(response.data);
      const responseData = record(responseRecord?.data);
      const candidate = responseRecord?.message ?? responseData?.message ?? responseData ?? response.data;
      if (!this.belongsToCustomerCare(response.data) || !this.belongsToCustomerCare(candidate)) throw new Error("The server returned a message from another conversation.");
      const confirmed = readSentMessage(response.data, this.options.senderId);
      if (confirmed && (confirmed.sender === "user" || (confirmed.sender !== "bot" && confirmed.content === text))) this.putMessages([{ ...confirmed, sender: "user", tempId, isOptimistic: false, deliveryStatus: "sent" }]);
      else {
        if (confirmed) this.putMessages([confirmed]);
        // Some deployments only return an acknowledgement; retain the accepted
        // message until history or its socket echo supplies the server ID.
        this.options.setMessages(this.options.getMessages().map(message => message.id === tempId ? { ...message, deliveryStatus: "sent" } : message));
        void this.refreshHistory(true);
      }
      this.update({ error: null });
      return true;
    } catch (error) {
      if (!this.current()) return false;
      const wasConfirmed = this.options.getMessages().some(message => message.tempId === tempId && !message.isOptimistic);
      if (wasConfirmed) return true;
      this.options.setMessages(this.options.getMessages().map(message => message.id === tempId ? { ...message, deliveryStatus: "failed" } : message));
      this.update({ error: failureMessage(error, "Delivery could not be confirmed. Refresh the conversation before trying again.") });
      return false;
    } finally {
      this.update({ isSending: false });
    }
  }

  typing(hasText: boolean) {
    if (!this.current() || !this.socket?.connected || !this.state.chatId) return;
    if (this.typingTimer) clearTimeout(this.typingTimer);
    if (!hasText) { this.stopTyping(); return; }
    if (!this.isTyping) this.socket.emit("typing", { chatId: this.state.chatId, sender: this.options.senderId });
    this.isTyping = true;
    this.typingTimer = setTimeout(() => this.stopTyping(), 1500);
  }

  stopTyping() {
    if (this.typingTimer) clearTimeout(this.typingTimer);
    this.typingTimer = null;
    if (this.isTyping && this.socket?.connected && this.state.chatId) this.socket.emit("stopTyping", { chatId: this.state.chatId, sender: this.options.senderId });
    this.isTyping = false;
  }

  dispose() {
    this.stopTyping();
    if (this.agentTypingTimer) clearTimeout(this.agentTypingTimer);
    // A cancelled HTTP request has uncertain delivery until the next history load.
    if (this.current()) this.options.setMessages(this.options.getMessages().map(message => message.deliveryStatus === "sending" ? { ...message, deliveryStatus: "failed" } : message));
    this.active = false;
    this.abort.abort();
    if (this.state.chatId) this.socket?.emit("leaveChat", this.state.chatId);
    this.socket?.removeAllListeners();
    this.socket?.disconnect();
    this.socket = null;
  }
}
